import React, {cloneElement, isValidElement, useCallback, useEffect, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {Wrapper} from '@googlemaps/react-wrapper'
import {useGlobalStore} from '../../store/hooks/useStore'
import {Box} from '../../vanilla'
import {StoreDetailResponseItem} from '../../types/store/storeDetails'
import {MarkerProps} from './marker'
import './styles.css'

interface StoreMapProps {
  mapOptions: {
    center: {
      lat: number
      lng: number
    }
    zoom: number
  }
  children: React.ReactNode
  updateBounce?: () => void
  onMarkerClick?: (store: StoreDetailResponseItem) => void
  onSetMap?: (map: google.maps.Map) => void
}

export const StoreMap = observer(function StoreMap(props: StoreMapProps) {
  const {mapOptions, updateBounce, onMarkerClick, onSetMap, children} = props
  const {googleMapsAPIkey} = useGlobalStore()

  const [map, setMap] = useState<google.maps.Map>()

  const onResize = () => {
    updateBounce?.()
    if (!map) return

    if ((map.getZoom() || 0) >= 17) map.setZoom(16)
  }

  useEffect(() => {
    if (map) {
      onResize()

      window.addEventListener('resize', onResize)
    }

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [map, updateBounce])

  useEffect(() => {
    if (mapOptions?.center && map) map.setCenter(mapOptions.center)
  }, [mapOptions.center])

  const markers = React.Children.map(children, (child) => {
    if (isValidElement<MarkerProps>(child)) {
      return cloneElement<MarkerProps>(child, {
        map: map,
        onMarkerClick: (store) => {
          onMarkerClick?.(store)
          map?.setCenter({lat: store.latitude, lng: store.longitude})
        },
      })
    }
  })

  const initMap = useCallback((node: HTMLElement | null) => {
    if (node) {
      const map = new window.google.maps.Map(node, {
        disableDefaultUI: true,
        scrollwheel: false,
        mapTypeId: 'roadmap',
        zoomControl: true,
        ...mapOptions,
      })
      setMap(map)
      onSetMap?.(map)
    }
  }, [])

  return (
    <Wrapper apiKey={googleMapsAPIkey}>
      <Box ref={initMap} width="full" height="full">
        {markers}
      </Box>
    </Wrapper>
  )
})
